import React, { useEffect, useState, useCallback } from "react";
import { getToken } from "../lib/api";
import StatusBadge, { ocrStatusVariant, docStatusVariant } from "./StatusBadge";
import {
  X, ZoomIn, ZoomOut, Maximize2, RotateCw, Download, FileText,
} from "lucide-react";

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 5;
const ZOOM_STEP = 0.25;

export default function DocumentPreviewModal({ document: doc, onClose }) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  const fileUrl = doc
    ? `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/file?auth=${encodeURIComponent(getToken() || "")}`
    : null;

  const isImage = doc && (doc.content_type || "").startsWith("image/");
  const isPdf = doc && (doc.content_type || "") === "application/pdf";

  const handleKey = useCallback(
    (e) => {
      if (!doc) return;
      if (e.key === "Escape") onClose();
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
      else if (e.key === "-") setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
      else if (e.key === "0") {
        setZoom(1);
        setRotation(0);
      }
    },
    [doc, onClose]
  );

  useEffect(() => {
    if (!doc) return;
    setZoom(1);
    setRotation(0);
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [doc, handleKey]);

  if (!doc) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-stretch justify-center"
      onClick={onClose}
      data-testid="preview-modal"
    >
      <div
        className="bg-white border border-[#E5E5E9] w-[96vw] h-[94vh] mt-[3vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E9]">
          <div className="min-w-0">
            <div className="ds-overline">Preview</div>
            <div className="font-display text-base font-bold tracking-tight truncate" title={doc.filename}>
              {doc.filename}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {doc.document_type && (
                <span className="ds-badge" style={{ background: "#F7F7F8", borderColor: "#E5E5E9", color: "#4A4A52" }}>
                  {doc.document_type}
                </span>
              )}
              <StatusBadge variant={ocrStatusVariant(doc.ocr_status)}>{doc.ocr_status}</StatusBadge>
              <StatusBadge variant={docStatusVariant(doc.status)}>{doc.status}</StatusBadge>
              <span className="font-mono text-[11px] text-[#8A8A93]">
                {(doc.size / 1024).toFixed(1)} KB · {new Date(doc.upload_date).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {(isImage || isPdf) && (
              <>
                <button
                  className="ds-btn-secondary !px-2"
                  onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
                  title="Zoom out (-)"
                  data-testid="preview-zoom-out"
                >
                  <ZoomOut size={14} />
                </button>
                <span className="font-mono text-[12px] w-14 text-center" data-testid="preview-zoom-level">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  className="ds-btn-secondary !px-2"
                  onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
                  title="Zoom in (+)"
                  data-testid="preview-zoom-in"
                >
                  <ZoomIn size={14} />
                </button>
                <button
                  className="ds-btn-secondary !px-2"
                  onClick={() => {
                    setZoom(1);
                    setRotation(0);
                  }}
                  title="Reset (0)"
                  data-testid="preview-reset"
                >
                  <Maximize2 size={14} />
                </button>
                {isImage && (
                  <button
                    className="ds-btn-secondary !px-2"
                    onClick={() => setRotation((r) => (r + 90) % 360)}
                    title="Rotate"
                    data-testid="preview-rotate"
                  >
                    <RotateCw size={14} />
                  </button>
                )}
                <span className="w-px h-6 bg-[#E5E5E9] mx-1" />
              </>
            )}
            <a
              className="ds-btn-secondary !px-2"
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              title="Open / download"
              data-testid="preview-download"
            >
              <Download size={14} />
            </a>
            <button
              className="ds-btn-secondary !px-2"
              onClick={onClose}
              title="Close (Esc)"
              data-testid="preview-close"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 grid grid-cols-12 min-h-0">
          {/* Viewer */}
          <div className="col-span-12 lg:col-span-8 bg-[#1a1a1a] overflow-auto flex items-start justify-center p-6 relative">
            {isImage && (
              <img
                src={fileUrl}
                alt={doc.filename}
                draggable={false}
                style={{
                  transform: `scale(${zoom}) rotate(${rotation}deg)`,
                  transformOrigin: "center top",
                  transition: "transform 120ms ease",
                  maxWidth: "none",
                  userSelect: "none",
                }}
                data-testid="preview-image"
              />
            )}
            {isPdf && (
              <div
                style={{
                  width: `${zoom * 100}%`,
                  minWidth: "60%",
                  height: "100%",
                  transition: "width 120ms ease",
                }}
              >
                <iframe
                  title={doc.filename}
                  src={fileUrl}
                  className="w-full h-full bg-white border-0"
                  data-testid="preview-pdf"
                />
              </div>
            )}
            {!isImage && !isPdf && (
              <div className="text-[#A0A0A8] text-sm flex flex-col items-center mt-20" data-testid="preview-unsupported">
                <FileText size={28} className="mb-3" />
                Preview not supported for <span className="font-mono">{doc.content_type}</span>.
                <a className="ds-btn-primary mt-4 inline-flex items-center gap-2" href={fileUrl} target="_blank" rel="noreferrer">
                  <Download size={13} /> Download to view
                </a>
              </div>
            )}
          </div>

          {/* Side panel */}
          <aside className="col-span-12 lg:col-span-4 border-l border-[#E5E5E9] p-4 overflow-y-auto">
            <div className="ds-overline">Metadata</div>
            <dl className="text-[12px] mt-1 space-y-1">
              <div><span className="text-[#8A8A93]">Type:</span> {doc.document_type || "—"}</div>
              <div><span className="text-[#8A8A93]">Source:</span> {doc.upload_source}</div>
              <div><span className="text-[#8A8A93]">Uploaded by:</span> {doc.uploaded_by_name || doc.uploaded_by}</div>
              <div><span className="text-[#8A8A93]">Confidence:</span> {(doc.ocr_confidence || 0).toFixed(2)}</div>
              <div><span className="text-[#8A8A93]">Year/Month:</span> {doc.year}/{String(doc.month).padStart(2, "0")}</div>
              <div className="font-mono text-[11px] break-all"><span className="text-[#8A8A93]">ID:</span> {doc.id}</div>
            </dl>

            <div className="ds-overline mt-4" style={{ fontSize: 10 }}>OCR text</div>
            <div
              className="font-mono text-[11.5px] whitespace-pre-wrap bg-[#F7F7F8] border border-[#E5E5E9] p-3 max-h-[60vh] overflow-y-auto"
              data-testid="preview-ocr-text"
            >
              {doc.ocr_text || <span className="text-[#8A8A93]">No OCR text.</span>}
            </div>

            <div className="ds-overline mt-4" style={{ fontSize: 10 }}>Shortcuts</div>
            <div className="text-[11px] text-[#4A4A52] font-mono leading-5">
              + / − &nbsp;zoom<br />0 &nbsp;reset<br />Esc &nbsp;close
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
